import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useCart } from "../context/CartContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { MapPin, Home, Plus, Truck, Store, CheckCircle } from "lucide-react";
import axios from "axios";
import { Badge } from "../components/ui/badge";
import { geocodeAddress } from "../services/mapService";
import AddressFormGroup from "../components/AddressFormGroup";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const DELIVERY_MODE_CONFIG = {
  STORE_DELIVERY: { label: "Standard", icon: "🏪" },
  SELLER: { label: "Standard", icon: "🏪" }, // Map SELLER type to Standard
  TRANSPORT: { label: "Transporteur", icon: "🚚" },
  CARRIER: { label: "Transporteur", icon: "🚚" },
  PICKUP: { label: "Retrait", icon: "📦" },
  EXPRESS: { label: "Express (24h)", icon: "⚡" },
};

export default function OrderPreviewPage() {
  const { itemsByStore, taxes, serviceFees, updateShippingForStore, selectedShipping, shippingAddress, setShippingAddress, deliveryModes, updateDeliveryMode } = useCart();
  const { user, isAuthenticated, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [deliveryOptionsMap, setDeliveryOptionsMap] = useState({});
  const [storeBaseOptions, setStoreBaseOptions] = useState({});
  const [, setCalculating] = useState(false);
  // Urgence par store : "normal" | "express" — modifiable via radio quand allow_express=true
  const [storeUrgency, setStoreUrgency] = useState({});

  // Address selection state
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [newAddressData, setNewAddressData] = useState({
    countryCode: 'ht', address: "", city: "", region: "", zipCode: "", address_details: "", lat: null, lng: null
  });
  const [newAddressName, setNewAddressName] = useState("");
  const [loading, setLoading] = useState(false);
  // Édition d'adresse existante
  const [editingAddr, setEditingAddr] = useState(null);  // { idx, data, name }

  const handleDeleteAddress = async (idx) => {
    const updated = (user?.saved_addresses || []).filter((_, i) => i !== idx);
    try {
      await axios.put(`${API_URL}/users/profile`, { saved_addresses: updated });
      await refreshUser();
      if (shippingAddress?.id === (user?.saved_addresses || [])[idx]?.id) {
        setShippingAddress(updated[0] || null);
      }
    } catch (e) { console.error(e); }
  };

  const handleSaveEdit = async () => {
    if (!editingAddr) return;
    setLoading(true);
    const coords = (editingAddr.data.lat && editingAddr.data.lng)
      ? { lat: editingAddr.data.lat, lon: editingAddr.data.lng }
      : await geocodeAddress([editingAddr.data.address, editingAddr.data.city, 'Haiti'].filter(Boolean).join(', ')).catch(() => null);

    const updatedAddr = {
      ...editingAddr.data, name: editingAddr.name,
      coordinates: coords || editingAddr.data.coordinates,
    };
    const updated = (user?.saved_addresses || []).map((a, i) =>
      i === editingAddr.idx ? updatedAddr : a
    );
    try {
      await axios.put(`${API_URL}/users/profile`, { saved_addresses: updated });
      await refreshUser();
      if (shippingAddress?.id === updatedAddr.id) {
        lastCalcKey.current = null;
        setShippingAddress(updatedAddr);
      }
      setEditingAddr(null);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleAddAddress = async () => {
    if (!newAddressData.address) return;
    const currentAddresses = user?.saved_addresses || [];
    const label = newAddressName || (currentAddresses.length === 0 ? "Maison" : "Autre");

    setLoading(true);
    const coords = (newAddressData.lat && newAddressData.lng)
      ? { lat: newAddressData.lat, lon: newAddressData.lng }
      : await geocodeAddress(newAddressData.address);

    const updatedAddresses = [...currentAddresses, {
      id: Date.now(),
      address: newAddressData.address,
      country_code: newAddressData.countryCode,
      city: newAddressData.city,
      region: newAddressData.region,
      zip_code: newAddressData.zipCode,
      address_details: newAddressData.address_details,
      name: label,
      coordinates: coords
    }];

    try {
      await axios.put(`${API_URL}/users/profile`, { saved_addresses: updatedAddresses });
      await refreshUser();
      setNewAddressData({
        countryCode: 'ht', address: "", city: "", region: "", zipCode: "", address_details: "", lat: null, lng: null
      });
      setNewAddressName("");
      setShippingAddress(updatedAddresses[updatedAddresses.length - 1]);
      toast.success("Adresse ajoutée");
    } catch (e) {
      toast.error("Erreur ajout adresse");
    } finally {
      setLoading(false);
    }
  };

  // ── Anti-loop : on mémorise la clé du dernier calcul ────────────────────────
  const lastCalcKey = React.useRef(null);

  const getCalcKey = React.useCallback(() => {
    const storeIds   = Object.keys(itemsByStore).sort().join(",");
    // Clé basée sur coordonnées GPS réelles — pas seulement l'id
    const lat = shippingAddress?.coordinates?.lat ?? 0;
    const lng = shippingAddress?.coordinates?.lon ?? shippingAddress?.coordinates?.lng ?? 0;
    const addrKey = `${shippingAddress?.id ?? shippingAddress?.address ?? "none"}_${lat}_${lng}`;
    const modesKey   = Object.entries(deliveryModes).sort().map(([k,v]) => `${k}:${v}`).join(",");
    const urgencyKey = Object.entries(storeUrgency).sort().map(([k,v]) => `${k}:${v}`).join(",");
    return `${storeIds}|${addrKey}|${modesKey}|${urgencyKey}`;
  }, [itemsByStore, shippingAddress, deliveryModes, storeUrgency]);

  const calculateAllDelivery = React.useCallback(async () => {
    const storeIds = Object.keys(itemsByStore);
    if (storeIds.length === 0) return;

    setCalculating(true);
    const newOptionsMap     = {};
    const newBaseOptionsMap = {};

    // Coordonnées client (défaut Port-au-Prince si pas d'adresse)
    const hasAddress = !!(shippingAddress?.coordinates?.lat);
    // Si pas de vraies coordonnées → envoyer null (pas le fallback PAP silencieux)
    // Le backend utilisera alors une distance conservatrice pour éviter "GRATUIT" erroné
    const clientLat  = shippingAddress?.coordinates?.lat  || null;
    const clientLng  = shippingAddress?.coordinates?.lon  || shippingAddress?.coordinates?.lng || null;

    const loc = {
      latitude:  clientLat,
      longitude: clientLng,
      address:   shippingAddress?.address || "Port-au-Prince",
    };

    await Promise.all(storeIds.map(async (storeId) => {
      const group = itemsByStore[storeId];
      if (!group?.items) return;

      // ── Filtrer les items avec un product.id valide ──────────────────────
      const validItems = group.items.filter(item => item.product?.id);
      if (validItems.length === 0) return;

      const deliveryItems = validItems.map(item => ({
        product_id:    item.product.id,
        quantity:      item.quantity,
        weight_lb:     parseFloat(item.product.delivery_settings?.weight || 0.5),
        // Use user selection, or fall back to the product's first allowed type, then STORE_DELIVERY
        selected_mode: deliveryModes[item.product.id]
          || item.product.allowed_delivery_types?.[0]
          || "STORE_DELIVERY",
      }));

      // Modes sélectionnés pour ce groupe
      const selectedModes = new Set(deliveryItems.map(i => i.selected_mode));
      const hasTransport  = selectedModes.has("TRANSPORT") || selectedModes.has("EXPRESS");
      // Modes que le transporteur NE couvre pas (Standard, Pickup, etc.)
      const nonTransportModes = new Set([...selectedModes].filter(m => m !== "TRANSPORT" && m !== "EXPRESS"));

      try {
        // Appels parallèles : standard + mixed (+ transporteur si besoin)
        const calls = [
          axios.post(`${API_URL}/delivery/calculate`, { store_id: storeId, items: deliveryItems, location: loc })
            .catch(() => ({ data: { options: [], delivery_fee: 0 } })),
          axios.post(`${API_URL}/delivery/calculate-mixed`, { store_id: storeId, items: deliveryItems, location: loc })
            .catch(() => ({ data: { delivery_fee: 0 } })),
        ];

        // Quote transporteur : urgence vient du choix de l'utilisateur (storeUrgency)
        // ou du mode sélectionné si pas encore choisi
        const defaultUrgency = selectedModes.has("EXPRESS") ? "express" : "normal";
        const storeUrgencyVal = storeUrgency[storeId] || defaultUrgency;

        if (hasTransport) {
          calls.push(
            axios.post(`${API_URL}/delivery/calculate-transport`, {
              store_id:  storeId,
              latitude:  clientLat,
              longitude: clientLng,
              urgency:   storeUrgencyVal,
              // passer les items pour calcul du poids exact
              items: deliveryItems.map(i => ({ weight_lb: i.weight_lb || 0.5 })),
            }).catch(() => null)
          );
        }

        const results = await Promise.all(calls);
        const [resStd, resMixed, resTransport] = results;

        let options = resStd.data?.options || [];
        newBaseOptionsMap[storeId] = [...options];

        // Option mixte — uniquement pour les modes NON-transporteur combinés
        const needsMixed = nonTransportModes.size > 1
          || (nonTransportModes.size === 1 && selectedModes.size > 1)
          || (nonTransportModes.size === 1 && !options.find(o => o.type === [...nonTransportModes][0]));
        if (needsMixed) {
          const modeLabels = Array.from(nonTransportModes).map(m => DELIVERY_MODE_CONFIG[m]?.label || m).join(" + ");
          const mixedFee    = resMixed.data?.delivery_fee ?? 0;
          const mixedOk     = resMixed.data?.is_eligible !== false && mixedFee < 999;
          options.unshift({
            id:       "mixed_delivery",
            name:     mixedOk ? `Livraison personnalisée (${modeLabels})` : `⚠️ Livraison non disponible`,
            price:    mixedOk ? mixedFee : 999,
            type:     mixedOk ? "MIXED" : "UNAVAILABLE",
            disabled: !mixedOk,
            details:  resMixed.data,
          });
        }

        // Option TransporteurPro — toujours affichée si mode TRANSPORT/EXPRESS
        if (hasTransport) {
          const urgency     = storeUrgencyVal;
          const q           = resTransport?.data;
          const isOutOfRange = q?.out_of_range === true || q?.available === false;
          const apiPrice    = (!isOutOfRange && q?.fee != null) ? q.fee : null;
          const price       = isOutOfRange ? 999 : (apiPrice ?? resMixed.data?.delivery_fee ?? 0);
          const carrierName = q?.carrier_name || "Transporteur";
          const transportOpt = {
            id:             `transport_${urgency}`,
            name:           isOutOfRange
                              ? `⚠️ ${carrierName} — Hors zone`
                              : urgency === "express" ? `⚡ ${carrierName} Express` : `🚚 ${carrierName}`,
            price,
            type:           urgency === "express" ? "EXPRESS" : "TRANSPORT",
            urgency,                              // source de vérité pour les radios
            estimated_time: q?.delivery_time || null,
            allow_express:  !!q?.allow_express && !isOutOfRange,
            is_estimated:   !hasAddress,          // vrai si l'adresse n'est pas encore définie
            details:        q || {},
            api_available:  !!q?.available,
            disabled:       isOutOfRange,         // affiche "Non disponible" en rouge
          };
          // Toujours mettre en tête pour les modes transport-only
          options = options.filter(o => o.type !== "TRANSPORT" && o.type !== "EXPRESS");
          options.unshift(transportOpt);
        }

        if (options.length === 0) {
          const fallbackFee = resMixed.data?.delivery_fee ?? 0;
          const fallbackOk  = resMixed.data?.is_eligible !== false && fallbackFee < 999;
          options = [{
            id:       "mixed_delivery",
            name:     fallbackOk ? `Livraison — ${group.storeName}` : `⚠️ Livraison non disponible`,
            price:    fallbackOk ? fallbackFee : 999,
            type:     fallbackOk ? "MIXED" : "UNAVAILABLE",
            disabled: !fallbackOk,
            details:  resMixed.data,
          }];
        }

        newOptionsMap[storeId] = options;
      } catch (e) {
        console.error(`Delivery calc error for store ${storeId}:`, e);
        newOptionsMap[storeId] = [{
          id: "fallback", name: "Livraison standard", price: 0, type: "STORE_DELIVERY",
        }];
      }
    }));

    setDeliveryOptionsMap(newOptionsMap);
    setStoreBaseOptions(newBaseOptionsMap);
    setCalculating(false);

    // Auto-sélection — une seule fois, sans passer par setTimeout
    storeIds.forEach(sid => {
      const opts = newOptionsMap[sid];
      if (opts?.length > 0) updateShippingForStore(sid, opts[0]);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsByStore, shippingAddress, deliveryModes, storeUrgency]);

  useEffect(() => {
    const key = getCalcKey();
    if (lastCalcKey.current === key) return;   // ← bloque la boucle infinie
    lastCalcKey.current = key;
    calculateAllDelivery();
  }, [getCalcKey, calculateAllDelivery]);

  const getOptions = (storeId) => {
    return deliveryOptionsMap[storeId] || [];
  };

  const handleProceed = () => {
    navigate("/checkout");
  };

  // Helper to generate dynamic delivery text
  const getDeliverySummaryText = () => {
    const methods = new Set();
    Object.values(selectedShipping).forEach(opt => {
      if (!opt) return;
      const carrierName = opt.details?.carrier_name || "Transporteur";
      if (opt.type === "TRANSPORT") methods.add(carrierName);
      else if (opt.type === "EXPRESS") methods.add(`${carrierName} Express`);
      else if (opt.type === "PICKUP")  methods.add("Retrait boutique");
      else if (opt.type === "MORESURE") methods.add("MoreSure");
      else methods.add("Livraison standard");
    });
    if (methods.size === 0) return "Calcul en cours...";
    return Array.from(methods).join(" + ");
  };

  // Calculate final total
  const finalTotal = Object.keys(itemsByStore).reduce((sum, storeId) => {
    const group = itemsByStore[storeId];
    const shipping = selectedShipping[storeId]?.price || 0;
    return sum + group.subtotal + shipping;
  }, (taxes || 0) + (serviceFees || 0));

  if (Object.keys(itemsByStore).length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Panier vide</h1>
          <Link to="/catalogue"><Button>Retour au catalogue</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <h1 className="text-3xl font-bold mb-8">Aperçu de la commande</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {Object.entries(itemsByStore).map(([storeId, group]) => {
              const options = getOptions(storeId);
              // Check if current selection is still in the actual calculated options
              const isSelectionValid = selectedShipping[storeId] && options.some(o => o.id === selectedShipping[storeId]?.id);
              const currentSelection = isSelectionValid ? selectedShipping[storeId] : options[0];

              return (
                <Card key={storeId} className="overflow-hidden border-orange-100 shadow-sm">
                  <CardHeader className="bg-orange-50/50 py-4 border-b border-orange-100">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Store className="h-5 w-5 text-orange-600" />
                      {group.storeName}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="space-y-4 mb-6">
                      {group.items.map((item, idx) => (
                        <div key={idx} className="flex gap-4">
                          <div className="w-16 h-16 bg-gray-100 rounded-md overflow-hidden flex-shrink-0">
                            <img src={item.product.images?.[0]} alt="" className="w-full h-full object-cover" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium line-clamp-1">{item.product.title}</h4>
                            <div className="text-sm text-gray-500">
                              Qté: {item.quantity} x {item.product.price} HTG
                            </div>
                            {/* Dynamic "Shipped By" Badge */}
                            {(() => {
                              const itemMode = deliveryModes[item.product.id]
                                || item.product.allowed_delivery_types?.[0];
                              const isTransport = itemMode === "TRANSPORT" || itemMode === "EXPRESS";
                              const isMoresure  = ["moresure_client", "moresure_free"].includes(item.product.delivery_settings?.type);
                              // Récupérer le nom du transporteur depuis l'option calculée
                              const transportOpt = isTransport
                                ? (deliveryOptionsMap[storeId] || []).find(o => o.type === itemMode || o.type === "TRANSPORT" || o.type === "EXPRESS")
                                : null;
                              const carrierName = transportOpt?.details?.carrier_name || "Transporteur";
                              const label = isMoresure
                                ? "Expédié par MoreSure 🚛"
                                : isTransport
                                  ? `Expédié par ${carrierName} 🚚`
                                  : `Expédié par ${group.storeName} 🏪`;
                              return (
                                <Badge variant="secondary" className="text-[10px] mt-1 font-normal bg-gray-100 text-gray-600 hover:bg-gray-100">
                                  {label}
                                </Badge>
                              );
                            })()}

                            {/* ITEM MODE SELECTOR */}
                            {item.product?.allowed_delivery_types?.length > 1 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {item.product.allowed_delivery_types.map(mode => {
                                  const cfg = DELIVERY_MODE_CONFIG[mode] || { label: mode, icon: "📦" };
                                  const isModeSelected = deliveryModes[item.product.id] === mode;

                                  // Fetch individual info if available
                                  let baseOpt = null;
                                  const sOpts = storeBaseOptions[storeId];
                                  if (sOpts) {
                                    if (mode === "STORE_DELIVERY") baseOpt = sOpts.find(o => o.type === "SELLER");
                                    else if (mode === "EXPRESS") baseOpt = sOpts.find(o => o.type === "EXPRESS");
                                    else if (mode === "PICKUP") baseOpt = sOpts.find(o => o.type === "PICKUP");
                                  }
                                  const timeStr = baseOpt?.estimated_time || (baseOpt?.estimated_days ? `${baseOpt.estimated_days}j` : '');
                                  const freeStr = baseOpt && baseOpt.price === 0 ? " (Gratuit)" : "";

                                  return (
                                    <button
                                      key={mode}
                                      onClick={() => {
                                        updateDeliveryMode(item.product.id, mode);
                                        // Synchroniser le radio Standard/Express selon le badge cliqué
                                        if (mode === 'EXPRESS') {
                                          setStoreUrgency(prev => ({ ...prev, [storeId]: 'express' }));
                                        } else if (mode === 'TRANSPORT') {
                                          setStoreUrgency(prev => ({ ...prev, [storeId]: 'normal' }));
                                        }
                                      }}
                                      className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border transition-all ${isModeSelected
                                        ? "bg-orange-500 text-white border-orange-500 font-semibold"
                                        : "bg-white text-gray-500 border-gray-200 hover:border-orange-200"
                                        }`}
                                    >
                                      <span>{cfg.icon}</span>
                                      <span>{cfg.label} {timeStr ? `(${timeStr})` : ''}{freeStr}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          <div className="font-bold">
                            {(item.product.price * item.quantity).toFixed(2)} HTG
                          </div>
                        </div>
                      ))}
                    </div>

                    <Separator className="my-4" />

                    <div>
                      <h4 className="font-semibold mb-3 flex items-center gap-2 text-sm uppercase text-gray-500">
                        <Truck className="h-4 w-4" /> Frais d'expédition ({group.storeName})
                      </h4>
                      {currentSelection ? (
                        <div className={`rounded-xl border ${currentSelection.disabled || currentSelection.type === 'UNAVAILABLE' ? 'bg-red-50/50 border-red-200' : 'bg-orange-50/50 border-orange-100'}`}>
                          {/* Option name + price */}
                          <div className="p-4 flex items-start gap-3">
                            {currentSelection.disabled || currentSelection.type === 'UNAVAILABLE'
                              ? <span className="text-red-500 mt-0.5 text-lg">⚠️</span>
                              : <Truck className="h-5 w-5 text-orange-500 mt-0.5 shrink-0" />}
                            <div className="flex-1">
                              <div className="flex justify-between items-center w-full">
                                <h4 className={`font-bold ${currentSelection.disabled ? 'text-red-700' : 'text-gray-900'}`}>
                                  {currentSelection.name}
                                </h4>
                                <span className={`font-bold text-lg ${currentSelection.disabled ? 'text-red-600' : 'text-orange-600'}`}>
                                  {currentSelection.disabled || currentSelection.type === 'UNAVAILABLE'
                                    ? "Non disponible"
                                    : currentSelection.price === 0
                                      ? "GRATUIT"
                                      : `${currentSelection.price.toFixed(2)} HTG`}
                                </span>
                              </div>
                              {/* Message hors zone */}
                              {(currentSelection.disabled || currentSelection.type === 'UNAVAILABLE') && (
                                <p className="text-xs text-red-600 mt-1 bg-red-50 border border-red-200 rounded px-2 py-1">
                                  {currentSelection.details?.message
                                    || "Cette boutique ne livre pas à votre adresse. Contactez le vendeur ou choisissez une autre adresse."}
                                </p>
                              )}
                              {currentSelection.type === 'MIXED' && currentSelection.details?.breakdown?.length > 0 && (
                                <div className="text-sm text-gray-500 mt-2 flex flex-col gap-1">
                                  {currentSelection.details.breakdown.map((b, i) => (
                                    <span key={i}>• {b}</span>
                                  ))}
                                </div>
                              )}
                              {(currentSelection.estimated_time || currentSelection.estimated_days) && currentSelection.type !== 'MIXED' && (
                                <div className="text-xs text-gray-500 mt-1">
                                  🕒 Temps estimé : {currentSelection.estimated_time || `${currentSelection.estimated_days} jours`}
                                </div>
                              )}
                              {currentSelection.is_estimated && (
                                <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2 flex items-center gap-1">
                                  📍 Estimation basée sur Port-au-Prince — ajoutez votre adresse pour un tarif précis
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Radios Standard / Express — synchronisés avec les badges de mode */}
                          {(currentSelection.type === 'TRANSPORT' || currentSelection.type === 'EXPRESS') && currentSelection.allow_express && (
                            <div className="border-t border-orange-100 px-4 py-3 flex gap-6">
                              {[
                                { value: "normal",  label: "Standard", icon: "🚚", modeKey: "TRANSPORT" },
                                { value: "express", label: "Express",  icon: "⚡", modeKey: "EXPRESS"   },
                              ].map(({ value, label, icon, modeKey }) => {
                                const activeUrgency = storeUrgency[storeId] || currentSelection.urgency || "normal";
                                const isActive = activeUrgency === value;
                                return (
                                  <label
                                    key={value}
                                    className="flex items-center gap-2 cursor-pointer select-none"
                                    onClick={() => {
                                      // 1. Mettre à jour l'urgence du store
                                      setStoreUrgency(prev => ({ ...prev, [storeId]: value }));
                                      // 2. Synchroniser les deliveryModes de tous les items TRANSPORT/EXPRESS du store
                                      const storeItems = itemsByStore[storeId]?.items || [];
                                      storeItems.forEach(item => {
                                        const currentMode = deliveryModes[item.product.id]
                                          || item.product.allowed_delivery_types?.[0];
                                        if (currentMode === 'TRANSPORT' || currentMode === 'EXPRESS') {
                                          updateDeliveryMode(item.product.id, modeKey);
                                        }
                                      });
                                    }}
                                  >
                                    <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
                                      isActive ? "border-orange-500" : "border-gray-300"
                                    }`}>
                                      {isActive && <span className="w-2 h-2 rounded-full bg-orange-500 block" />}
                                    </span>
                                    <span className={`text-sm font-medium ${isActive ? "text-orange-600" : "text-gray-600"}`}>
                                      {icon} {label}
                                    </span>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500 italic">Calcul en cours...</div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="lg:col-span-1 space-y-6">
            {/* Delivery Address Card */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-gray-500" />
                  Adresse de livraison
                </h3>

                {isAuthenticated ? (
                  shippingAddress ? (
                    <div className="bg-gray-50 p-3 rounded-lg mb-4 border">
                      <div className="flex items-center gap-2 mb-1">
                        <Home className="h-3 w-3 text-gray-500" />
                        <span className="font-bold text-sm">{shippingAddress.name || "Mon Adresse"}</span>
                      </div>
                      <p className="text-gray-700">{shippingAddress.address}</p>
                    </div>
                  ) : (
                    <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 p-3 rounded-lg mb-4">
                      <p className="font-semibold mb-0.5">Aucune adresse sélectionnée</p>
                      <p className="text-xs text-amber-600">Les frais de livraison sont estimés à partir de Port-au-Prince.</p>
                    </div>
                  )
                ) : (
                  <div className="text-sm text-gray-500 mb-4">Connectez-vous pour gérer vos adresses.</div>
                )}

                <Dialog open={addressDialogOpen} onOpenChange={setAddressDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full" disabled={!isAuthenticated}>
                      {shippingAddress ? "Changer d'adresse" : "Ajouter une adresse"}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Mes adresses</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      {user?.saved_addresses?.map((addr, idx) => (
                        <div
                          key={idx}
                          className={`p-3 border rounded cursor-pointer flex justify-between items-center ${shippingAddress?.id === addr.id ? 'border-orange-500 bg-orange-50' : 'hover:bg-gray-50'}`}
                          onClick={async () => {
                            lastCalcKey.current = null; // forcer recalcul
                            let selectedAddr = addr;
                            // Si l'adresse n'a pas de coordonnées GPS, on tente le géocodage
                            if (!addr.coordinates?.lat) {
                              try {
                                const addressStr = [addr.address, addr.city, 'Haiti'].filter(Boolean).join(', ');
                                const coords = await geocodeAddress(addressStr);
                                if (coords?.lat) {
                                  selectedAddr = { ...addr, coordinates: coords };
                                  // Sauvegarder les coordonnées dans le profil
                                  const updated = (user?.saved_addresses || []).map(a =>
                                    a.id === addr.id ? selectedAddr : a
                                  );
                                  axios.put(`${API_URL}/users/profile`, { saved_addresses: updated })
                                    .catch(() => {});
                                }
                              } catch (e) { /* non-bloquant */ }
                            }
                            setShippingAddress(selectedAddr);
                            setAddressDialogOpen(false);
                          }}
                        >
                          <div>
                            <div className="font-semibold text-sm flex items-center gap-2">
                              {addr.name || "Adresse"}
                              {shippingAddress?.id === addr.id && <CheckCircle className="h-3 w-3 text-orange-500" />}
                            </div>
                            <div className="text-gray-500 text-xs">{addr.city ? `${addr.address}, ${addr.city}` : addr.address}</div>
                          </div>
                          <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                            <button className="text-xs text-indigo-600 hover:text-indigo-800 font-medium px-2 py-1 rounded hover:bg-indigo-50"
                              onClick={() => setEditingAddr({ idx, name: addr.name || "", data: {
                                countryCode: addr.country_code || 'ht', address: addr.address || "",
                                city: addr.city || "", region: addr.region || "",
                                zipCode: addr.zip_code || "", address_details: addr.address_details || "",
                                lat: addr.coordinates?.lat || null,
                                lng: addr.coordinates?.lon || addr.coordinates?.lng || null,
                              }})}>✏️ Modifier</button>
                            <button className="text-xs text-red-500 hover:text-red-700 font-medium px-2 py-1 rounded hover:bg-red-50"
                              onClick={() => handleDeleteAddress(idx)}>🗑️</button>
                          </div>
                        </div>
                      ))}

                      {/* Formulaire d'édition inline */}
                      {editingAddr && (
                        <div className="space-y-3 border border-indigo-200 rounded-xl p-4 bg-indigo-50/40">
                          <div className="flex justify-between items-center">
                            <p className="font-bold text-sm text-indigo-800">Modifier l'adresse</p>
                            <button className="text-xs text-gray-400 hover:text-red-500" onClick={() => setEditingAddr(null)}>✕ Annuler</button>
                          </div>
                          <div>
                            <Label className="text-xs mb-1 block">Nom de l'adresse</Label>
                            <Input value={editingAddr.name} placeholder="Maison, Bureau…"
                              onChange={e => setEditingAddr(p => ({ ...p, name: e.target.value }))}
                              className="bg-white h-8 text-sm" />
                          </div>
                          <AddressFormGroup
                            addressData={editingAddr.data}
                            onChange={data => setEditingAddr(p => ({ ...p, data }))} />
                          <Button onClick={handleSaveEdit} disabled={loading}
                            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white h-9 text-sm">
                            {loading ? "Sauvegarde…" : "Enregistrer les modifications"}
                          </Button>
                        </div>
                      )}

                      <div className="space-y-4 pt-2">
                        {!showNewAddressForm ? (
                          <Button
                            variant="ghost"
                            className="w-full border-dashed border-2 py-8 flex-col gap-2 hover:bg-orange-50 hover:border-orange-300 transition-all"
                            onClick={() => setShowNewAddressForm(true)}
                          >
                            <Plus className="h-6 w-6 text-orange-500" />
                            <span className="font-semibold text-orange-600">Ajouter une nouvelle adresse</span>
                          </Button>
                        ) : (
                          <div className="space-y-4 border p-4 rounded-xl bg-gray-50/50 animate-in fade-in slide-in-from-top-4 duration-300">
                            <div className="flex justify-between items-center">
                              <Label className="font-bold text-gray-700">Nouvelle adresse</Label>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setShowNewAddressForm(false)}
                                className="h-8 text-gray-500 hover:text-red-500"
                              >
                                Annuler
                              </Button>
                            </div>

                            <div className="space-y-2">
                              <Label className="text-xs text-gray-500">Nom de l'adresse</Label>
                              <Input
                                placeholder="Maison, Bureau, etc."
                                value={newAddressName}
                                onChange={(e) => setNewAddressName(e.target.value)}
                                className="bg-white"
                              />
                            </div>

                            <div className="space-y-2">
                              <Label className="text-xs text-gray-500">Détails de localisation</Label>
                              <AddressFormGroup
                                addressData={newAddressData}
                                onChange={(newData) => setNewAddressData(newData)}
                              />
                            </div>

                            <Button
                              onClick={async () => {
                                await handleAddAddress();
                                setShowNewAddressForm(false);
                              }}
                              className="w-full bg-orange-500 hover:bg-orange-600"
                              disabled={loading || !newAddressData.address}
                            >
                              {loading ? "Enregistrement…" : "Enregistrer l'adresse"}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>

            <Card className="sticky top-24 shadow-lg border-t-4 border-t-orange-500">
              <CardHeader>
                <CardTitle>Récapitulatif</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between">
                  <span>Sous-total</span>
                  <span>{Object.values(itemsByStore).reduce((sum, g) => sum + g.subtotal, 0).toFixed(2)} HTG</span>
                </div>
                <div className="flex justify-between">
                  <span>Frais de livraison</span>
                  <span>{Object.values(itemsByStore).reduce((sum, g) => {
                    const sid = g.storeId;
                    return sum + (selectedShipping[sid]?.price || 0);
                  }, 0).toFixed(2)} HTG</span>
                </div>
                <div className="flex justify-between">
                  <span>Taxes</span>
                  <span>{(taxes || 0).toFixed(2)} HTG</span>
                </div>
                <Separator />
                <div className="flex justify-between font-bold text-2xl">
                  <span>Total</span>
                  <span className="text-orange-600">{finalTotal.toFixed(2)} HTG</span>
                </div>

                <Button
                  className="w-full bg-orange-500 hover:bg-orange-600 text-lg py-6 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  onClick={handleProceed}
                  disabled={Object.keys(itemsByStore).some(storeId => {
                    const currentSelection = selectedShipping[storeId] || getOptions(storeId)[0];
                    return !currentSelection || currentSelection.disabled;
                  })}
                >
                  Passer la commande
                </Button>

                {/* Dynamic Delivery Info Text */}
                <div className="bg-blue-50 p-3 rounded-lg flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-bold text-blue-800 uppercase">Service Livraison</p>
                    <p className="text-xs text-blue-700">{getDeliverySummaryText()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
